import { Injectable, inject } from '@angular/core';
import { Observable, of, delay, map, catchError, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import {
  PurchaseRequestResponse,
  PurchaseRequestPurpose,
  PURCHASE_REQUEST_STATUS_OPTIONS,
  PURCHASE_REQUEST_PURPOSE_OPTIONS,
  COMPANY_OPTIONS,
  PURCHASE_REQUEST_DEFAULT_CONFIG,
} from './purchase-request.config';
import { PurchaseRequestControllerService } from '@app/api/purchase-service';
import { PageInfoPurchaseRequestResultDTO } from '@app/api/purchase-service/model/pageInfoPurchaseRequestResultDTO';
import { PurchaseRequestResultDTO } from '@app/api/purchase-service/model/purchaseRequestResultDTO';
import { PurchaseRequestItemResultDTO as ApiPurchaseRequestItem } from '@app/api/purchase-service/model/purchaseRequestItemResultDTO';
import { PurchaseRequestDTO } from '@app/api/purchase-service/model/purchaseRequestDTO';
import { PurchaseRequestUpdateDTO } from '@app/api/purchase-service/model/purchaseRequestUpdateDTO';
import { PurchaseRequestRecord } from './mock-data/purchase-request.mock-data';
import { formatDateWithCurrentTime } from '../procurement-date.util';

export interface PurchaseRequestSearchParams {
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
  id?: number;
  title?: string;
  series?: string;
  beginTransactionDate?: string;
  endTransactionDate?: string;
  beginScheduleDate?: string;
  endScheduleDate?: string;
  status?: 'PENDING' | 'REJECTED' | 'APPROVED';
  deleteFlag?: boolean;
  // Legacy fields for backward compatibility
  purpose?: string;
  company?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PurchaseRequestService {
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly purchaseRequestController = inject(
    PurchaseRequestControllerService
  );

  constructor() {
    // Service initialized
  }

  /**
   * Get purchase request records with search and pagination
   */
  getPurchaseRequestRecords(
    params: PurchaseRequestSearchParams
  ): Observable<PurchaseRequestResponse> {
    const {
      page = 0,
      size = PURCHASE_REQUEST_DEFAULT_CONFIG.PAGE_SIZE,
      sortBy = 'creationDate',
      ascending = false,
      ...searchParams
    } = params;

    // Map legacy parameters to API parameters
    const apiParams = this.mapSearchParamsToApiParams(
      searchParams,
      page,
      size,
      sortBy,
      ascending
    );

    return (
      this.purchaseRequestController.page(
        apiParams.id,
        apiParams.title,
        apiParams.series,
        apiParams.beginScheduleDate,
        apiParams.endScheduleDate,
        apiParams.status,
        apiParams.page,
        apiParams.size,
        apiParams.sortBy,
        apiParams.ascending,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      ) as Observable<PageInfoPurchaseRequestResultDTO>
    ).pipe(
      map((response: any) => {
        return this.mapApiResponseToLocalResponse(
          response as PageInfoPurchaseRequestResultDTO
        );
      }),
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'purchase_request.messages.failed_to_load_data'
          ),
          life: 5000,
        });

        // Fallback to mock data
        return throwError(() => error);
      })
    );
  }

  /**
   * Get purchase request by ID
   */
  getPurchaseRequestById(id: number): Observable<PurchaseRequestRecord | null> {
    return this.purchaseRequestController
      .getDetail(id, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          return this.mapApiItemToLocalRecord(
            response as PurchaseRequestResultDTO
          );
        }),
        catchError((error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.not_found'
            ),
            life: 5000,
          });

          // Fallback to mock data
          return throwError(() => error);
        })
      );
  }

  /**
   * Create new purchase request
   */
  createPurchaseRequest(
    data: PurchaseRequestDTO
  ): Observable<PurchaseRequestRecord> {
    const request: PurchaseRequestDTO = {
      ...data,
      scheduleDate: this.formatScheduleDate(data.scheduleDate),
    };

    return this.purchaseRequestController
      .addPurchaseRequest(request, undefined, undefined, {
        httpHeaderAccept: 'application/json' as '*/*',
      })
      .pipe(
        map((response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_request.messages.created_successfully'
            ),
            life: 3000,
          });
          return this.mapApiItemToLocalRecord(
            response as PurchaseRequestResultDTO
          );
        })
      );
  }

  /**
   * Update purchase request
   */
  updatePurchaseRequest(
    id: number,
    data: PurchaseRequestUpdateDTO
  ): Observable<PurchaseRequestRecord> {
    const request: PurchaseRequestUpdateDTO = {
      ...data,
      scheduleDate: this.formatScheduleDate(data.scheduleDate),
    };

    return this.purchaseRequestController.update(id, request).pipe(
      map((response) => this.mapApiItemToLocalRecord(response)),
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'purchase_request.messages.failed_to_update'
          ),
        });
        return throwError(() => error);
      })
    );
  }

  private formatScheduleDate(scheduleDate?: string): string | undefined {
    return scheduleDate
      ? formatDateWithCurrentTime(scheduleDate)
      : scheduleDate;
  }

  /**
   * Delete purchase request
   */
  deletePurchaseRequest(id: number): Observable<boolean> {
    return this.purchaseRequestController.deletePurchaseRequest(id).pipe(
      map(() => true),
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'purchase_request.messages.failed_to_delete'
          ),
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Update purchase request status (approve/reject)
   */
  updatePurchaseRequestStatus(
    id: number,
    status: 'PENDING' | 'APPROVED' | 'REJECTED'
  ): Observable<PurchaseRequestRecord> {
    return this.purchaseRequestController
      .approvePurchaseRequest(id, status)
      .pipe(
        map((response) => this.mapApiItemToLocalRecord(response)),
        catchError((error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_update_status'
            ),
          });
          return throwError(() => error);
        })
      );
  }

  /**
   * Get status options with translations
   */
  getStatusOptions(): Observable<any[]> {
    return of(
      PURCHASE_REQUEST_STATUS_OPTIONS.map((option) => ({
        ...option,
        label: this.translateService.instant(
          `purchase_request.status.${option.value.toLowerCase()}`
        ),
      }))
    ).pipe(delay(200));
  }

  /**
   * Get purpose options with translations
   */
  getPurposeOptions(): Observable<any[]> {
    return of(
      PURCHASE_REQUEST_PURPOSE_OPTIONS.map((option) => ({
        ...option,
        label: this.translateService.instant(
          `purchase_request.purpose.${option.value.toLowerCase()}`
        ),
      }))
    ).pipe(delay(200));
  }

  /**
   * Get company options
   */
  getCompanyOptions(): Observable<any[]> {
    return of(COMPANY_OPTIONS).pipe(delay(200));
  }

  /**
   * Get warehouse options (mock data)
   */
  getWarehouseOptions(): Observable<any[]> {
    const warehouses = [
      { label: 'Main Warehouse', value: 'MAIN_WH' },
      { label: 'Production Warehouse', value: 'PROD_WH' },
      { label: 'IT Warehouse', value: 'IT_WH' },
      { label: 'Manufacturing Warehouse', value: 'MFG_WH' },
      { label: 'Customer Warehouse', value: 'CUST_WH' },
    ];
    return of(warehouses).pipe(delay(200));
  }

  /**
   * Map API items to PurchaseRequestItem format for components
   */
  mapApiItemsToPurchaseRequestItems(apiItems: any[]): any[] {
    if (!apiItems || !Array.isArray(apiItems)) {
      return [];
    }

    return apiItems.map((apiItem) =>
      this.mapApiItemToPurchaseRequestItem(apiItem)
    );
  }

  /**
   * Filter data based on search parameters
   */
  private filterData(
    data: PurchaseRequestRecord[],
    searchParams: Partial<PurchaseRequestSearchParams>
  ): PurchaseRequestRecord[] {
    return data.filter((item) => {
      if (
        searchParams.id &&
        !item.id.toString().includes(searchParams.id.toString())
      ) {
        return false;
      }
      if (
        searchParams.title &&
        !item.title.toLowerCase().includes(searchParams.title.toLowerCase())
      ) {
        return false;
      }
      if (searchParams.purpose && item.purpose !== searchParams.purpose) {
        return false;
      }
      if (searchParams.company && item.company !== searchParams.company) {
        return false;
      }
      if (searchParams.status && item.status !== searchParams.status) {
        return false;
      }
      if (
        searchParams.createdDateFrom &&
        item.creationDate < searchParams.createdDateFrom
      ) {
        return false;
      }
      if (
        searchParams.createdDateTo &&
        item.creationDate > searchParams.createdDateTo
      ) {
        return false;
      }
      return true;
    });
  }

  /**
   * Sort data based on sort field and order
   */
  private sortData(
    data: PurchaseRequestRecord[],
    sortBy: string,
    ascending: boolean
  ): PurchaseRequestRecord[] {
    return [...data].sort((a, b) => {
      let aValue: any = (a as any)[sortBy];
      let bValue: any = (b as any)[sortBy];

      // Handle date fields
      if (sortBy === 'creationDate' || sortBy === 'lastUpdateDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle numeric fields
      if (sortBy === 'id' || sortBy === 'budget') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return ascending ? -1 : 1;
      }
      if (aValue > bValue) {
        return ascending ? 1 : -1;
      }
      return 0;
    });
  }

  /**
   * Paginate data
   */
  private paginateData(
    data: PurchaseRequestRecord[],
    page: number,
    size: number
  ): { data: PurchaseRequestRecord[]; total: number } {
    const start = page * size;
    const end = start + size;
    return {
      data: data.slice(start, end),
      total: data.length,
    };
  }

  /**
   * Map search parameters to API parameters
   */
  private mapSearchParamsToApiParams(
    searchParams: Partial<PurchaseRequestSearchParams>,
    page: number,
    size: number,
    sortBy: string,
    ascending: boolean
  ) {
    return {
      id: searchParams.id,
      title: searchParams.title,
      series: searchParams.series,
      beginScheduleDate: searchParams.beginScheduleDate,
      endScheduleDate: searchParams.endScheduleDate,
      status: searchParams.status,
      deleteFlag: searchParams.deleteFlag,
      page: page,
      size: size,
      sortBy: sortBy,
      ascending: ascending,
    };
  }

  /**
   * Map API response to local response structure
   */
  private mapApiResponseToLocalResponse(
    apiResponse: PageInfoPurchaseRequestResultDTO
  ): any {
    // Map each item in pageContent through mapApiItemToLocalRecord
    const mappedPageContent = (apiResponse.pageContent || []).map(
      (item: PurchaseRequestResultDTO) => this.mapApiItemToLocalRecord(item)
    );

    const mappedResponse = {
      pageContent: mappedPageContent,
      number: apiResponse.number || 0,
      size: apiResponse.size || 0,
      totalElements: apiResponse.totalElements || 0,
      totalPages: apiResponse.totalPages || 0,
      empty: apiResponse.empty || false,
      numberOfElements: apiResponse.numberOfElements || 0,
      hasNextPage: apiResponse.hasNextPage || false,
      hasPreviousPage: apiResponse.hasPreviousPage || false,
    };
    return mappedResponse;
  }

  /**
   * Map API item to local record structure
   */
  private mapApiItemToLocalRecord(
    apiItem: PurchaseRequestResultDTO
  ): PurchaseRequestRecord {
    // Calculate hasActiveOrders and get all active orders
    const hasActiveOrders = this.calculateHasActiveOrders(apiItem.orders || []);
    const activeOrders = this.getAllActiveOrders(apiItem.orders || []);
    const activeOrder = this.getFirstActiveOrder(apiItem.orders || []);

    const mappedRecord = {
      id: apiItem.id || 0,
      title: apiItem.title || '',
      series: apiItem.series || '',
      scheduleDate: apiItem.scheduleDate || '', // Use actual schedule date from API
      status: this.mapApiStatusToLocalStatus(apiItem.status),
      deleteFlag: apiItem.deleteFlag || false,
      creationDate: apiItem.creationDate || '',
      lastUpdateDate: apiItem.lastUpdateDate || '',
      createdBy: apiItem.createdBy || '',
      lastUpdatedBy: apiItem.lastUpdatedBy || '',
      objectVersionNumber: apiItem.objectVersionNumber || 0,
      items:
        apiItem.items?.map((item: any) => this.mapApiItemToLocalItem(item)) ||
        [],
      orders: apiItem.orders || [],
      hasActiveOrders: hasActiveOrders,
      activeOrders: activeOrders,
      activeOrder: activeOrder,
      // Legacy fields for backward compatibility
      purpose: PurchaseRequestPurpose.PURCHASE,
      company: '',
      warehouse: '',
      description: '',
      approvedBy: '',
      approvedDate: '',
      createdDate: apiItem.creationDate || '',
      lastModified: apiItem.lastUpdateDate || '',
    };
    return mappedRecord;
  }

  /**
   * Calculate if there are any active (non-deleted) orders
   * @param orders Array of purchase orders
   * @returns true if there are active orders, false otherwise
   */
  private calculateHasActiveOrders(orders: any[]): boolean {
    if (!orders || orders.length === 0) {
      return false;
    }

    // Filter out orders with deleteFlag = true
    const activeOrders = orders.filter((order) => !order.deleteFlag);
    return activeOrders.length > 0;
  }

  /**
   * Get all active (non-deleted) purchase orders
   * @param orders Array of purchase orders
   * @returns Array of active orders
   */
  private getAllActiveOrders(orders: any[]): any[] {
    if (!orders || orders.length === 0) {
      return [];
    }

    // Filter out orders with deleteFlag = true
    return orders.filter((order) => !order.deleteFlag);
  }

  /**
   * Get the first active (non-deleted) purchase order
   * @param orders Array of purchase orders
   * @returns First active order or null if none exists
   */
  private getFirstActiveOrder(orders: any[]): any {
    if (!orders || orders.length === 0) {
      return null;
    }

    // Find the first order with deleteFlag = false or undefined
    return orders.find((order) => !order.deleteFlag) || null;
  }

  /**
   * Map API item to local item structure (for mock data compatibility)
   */
  private mapApiItemToLocalItem(apiItem: ApiPurchaseRequestItem): any {
    return {
      creationDate: apiItem.creationDate || '',
      lastUpdateDate: apiItem.lastUpdateDate || '',
      createdBy: apiItem.createdBy || '',
      lastUpdatedBy: apiItem.lastUpdatedBy || '',
      objectVersionNumber: apiItem.objectVersionNumber || 0,
      id: apiItem.id || 0,
      purchaseRequest: apiItem.purchaseRequest || 0,
      skuCode: apiItem.skuCode || '',
      scheduleDate: apiItem.scheduleDate || '', // Use actual schedule date from API
      quantity: apiItem.quantity || 0,
      description: apiItem.description || '',
      deleteFlag: apiItem.deleteFlag || false,
      spuCode: apiItem.spuCode || '',
      spuName: apiItem.spuName || '',
      skuName: apiItem.skuName || '',
      warehouseCode: apiItem.warehouseCode || '',
    };
  }

  /**
   * Map API item to PurchaseRequestItem interface (for component usage)
   */
  private mapApiItemToPurchaseRequestItem(apiItem: any): any {
    return {
      id: apiItem.id?.toString() || '0', // Preserve original ID or use '0' for new items
      spuCode: apiItem.spuCode || '',
      spuName: apiItem.spuName || '',
      skuCode: apiItem.skuCode || '', // Use skuCode from API response
      skuName: apiItem.skuName || '',
      quantity: apiItem.quantity || 0,
      scheduleDate: apiItem.scheduleDate || '', // Use actual schedule date from API
      warehouseCode: apiItem.warehouseCode || '',
    };
  }

  /**
   * Map API status to local status
   */
  private mapApiStatusToLocalStatus(
    status?: string | null
  ): 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' {
    if (status === 'APPROVED') {
      return 'APPROVED';
    } else if (status === 'REJECTED') {
      return 'REJECTED';
    } else {
      return 'PENDING_APPROVAL';
    }
  }

  /**
   * Map local record to API DTO
   */
  private mapLocalRecordToApiDTO(data: any): any {
    // Handle both direct items property and items from PurchaseRequestRecord
    let items = [];
    if (data.items && Array.isArray(data.items) && data.items.length > 0) {
      // Check if first item has API format properties
      const firstItem = data.items[0];
      if (firstItem.itemCode || firstItem.skuName) {
        // Already in API format
        items = data.items;
      } else {
        // Need to map to API format
        items = data.items.map((item: any) => ({
          itemCode: item.skuCode || '',
          skuName: item.skuName || '',
          spuCode: item.spuCode || '',
          spuName: item.spuName || '',
          scheduleDate: item.scheduleDate || '', // Use actual schedule date from data
          quantity: item.quantity || 0,
          warehouseCode: item.warehouseCode || '',
        }));
      }
    }

    return {
      title: data.title || '',
      series: data.series || '',
      scheduleDate: data.scheduleDate || '', // Use actual schedule date from data
      terms: data.terms || 'Net 30',
      termsContent: data.termsContent || '',
      letterHead: data.letterHead || 'Main Office',
      printHeading: data.printHeading || '',
      items: items,
    };
  }
}
